<script setup lang="ts">
  import { ref } from 'vue'

  // Import UI components
  import { Button } from '@/components/ui/button'
  // Import icons
  import { PlusIcon, ArrowRightIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
  import { DownloadIcon, SendIcon, TrashIcon } from 'lucide-vue-next'
  import { Input } from '@/components/ui/input'
  import { useThemeColor } from '@/composables/useThemeColorV3'

  // Import form components
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import MultiSelectField from '@/components/admin/form/MultiSelectField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import DatePickerField from '@/components/admin/form/DatePickerField.vue'
  import FileField from '@/components/admin/form/FileField.vue'
  import MultiFileField from '@/components/admin/form/MultiFileField.vue'
  import FormGrid from '@/components/admin/form/FormGrid.vue'
  import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle
  } from '@/components/ui/card'
  import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
  } from '@/components/ui/accordion'
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
  } from '@/components/ui/select'
  import { Checkbox } from '@/components/ui/checkbox'
  import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
  import { Switch } from '@/components/ui/switch'
  import { Badge } from '@/components/ui/badge'
  import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
  import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
  } from '@/components/ui/table'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
  } from '@/components/ui/alert-dialog'

  // Component categories
  const categories = ref([
    {
      id: 'buttons',
      name: 'Buttons',
      description: 'Button components for various actions and states'
    },
    {
      id: 'form',
      name: 'Form Components',
      description: 'Form components for data entry and validation'
    },
    {
      id: 'inputs',
      name: 'Shadcn UI Inputs',
      description: 'Shadcn UI input components for data entry'
    },
    {
      id: 'cards',
      name: 'Cards',
      description: 'Card components for displaying content'
    },
    {
      id: 'accordion',
      name: 'Accordion',
      description: 'Vertically collapsing accordion components'
    },
    {
      id: 'alerts',
      name: 'Alerts & Badges',
      description: 'Alert and badge components for notifications and status'
    },
    {
      id: 'tabs',
      name: 'Tabs',
      description: 'Tab components for organizing content'
    },
    {
      id: 'tables',
      name: 'Tables',
      description: 'Table components for data display'
    }
  ])

  // Active category
  const activeCategory = ref('buttons')

  // Function to set active category
  const setActiveCategory = (categoryId: string) => {
    activeCategory.value = categoryId
  }

  // Sample data for examples
  const checkboxValue = ref(false)
  const switchValue = ref(false)
  const selectValue = ref('')
  const textareaValue = ref('')
  const inputValue = ref('')
  const radioValue = ref('option1')

  // Theme color
  const themeColor = useThemeColor()
</script>

<template>
  <div class="ui-components-library">
    <!-- Header -->
    <div class="mb-6 px-4 sm:px-6 lg:px-8">
      <h1 class="text-2xl font-semibold text-gray-900">UI Components Library</h1>
      <p class="mt-2 text-sm text-gray-600">
        A comprehensive library of UI components used throughout the application for reference by
        the UX/UI team.
      </p>
    </div>

    <div class="flex flex-col lg:flex-row">
      <!-- Sidebar Navigation -->
      <div class="w-full border-r border-gray-200 lg:w-64 lg:flex-shrink-0">
        <div class="p-4">
          <h2 class="text-xs font-semibold uppercase tracking-wider text-gray-500">Categories</h2>
          <nav class="mt-4 space-y-1">
            <button
              v-for="category in categories"
              :key="category.id"
              :class="[
                activeCategory === category.id
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                'group flex w-full items-center rounded-md px-3 py-2 text-sm font-medium'
              ]"
              @click="setActiveCategory(category.id)"
            >
              {{ category.name }}
            </button>
          </nav>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="min-w-0 flex-1 p-4 sm:p-6">
        <!-- Buttons Section -->
        <div
          v-if="activeCategory === 'buttons'"
          class="space-y-8"
        >
          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Button Variants</h2>
            <div class="flex flex-wrap gap-4">
              <Button>Default Button</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="link">Link</Button>
              <Button disabled>Disabled</Button>
            </div>
            <div class="mt-4">
              <pre
                class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
              ><code>&lt;Button&gt;Default Button&lt;/Button&gt;
&lt;Button variant="secondary"&gt;Secondary&lt;/Button&gt;
&lt;Button variant="destructive"&gt;Destructive&lt;/Button&gt;
&lt;Button variant="ghost"&gt;Ghost&lt;/Button&gt;
&lt;Button variant="link"&gt;Link&lt;/Button&gt;
&lt;Button disabled&gt;Disabled&lt;/Button&gt;</code></pre>
            </div>
          </div>

          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Button Sizes</h2>
            <div class="flex flex-wrap items-center gap-4">
              <Button size="sm">Small</Button>
              <Button size="default">Default</Button>
              <Button size="lg">Large</Button>
              <Button size="icon">🔍</Button>
            </div>
            <div class="mt-4">
              <pre
                class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
              ><code>&lt;Button size="sm"&gt;Small&lt;/Button&gt;
&lt;Button size="default"&gt;Default&lt;/Button&gt;
&lt;Button size="lg"&gt;Large&lt;/Button&gt;
&lt;Button size="icon"&gt;🔍&lt;/Button&gt;</code></pre>
            </div>
          </div>

          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Buttons with Icons</h2>
            <div class="flex flex-wrap items-center gap-4">
              <Button>
                <PlusIcon class="mr-2 h-4 w-4" />
                Add Item
              </Button>
              <Button variant="secondary">
                <DownloadIcon class="mr-2 h-4 w-4" />
                Download
              </Button>
              <Button variant="ghost">
                <SendIcon class="mr-2 h-4 w-4" />
                Send
              </Button>
              <Button variant="destructive">
                <TrashIcon class="mr-2 h-4 w-4" />
                Delete
              </Button>
              <Button size="default">
                <ArrowRightIcon class="mr-2 h-3 w-3" />
                Continue
              </Button>
              <Button size="icon">
                <MagnifyingGlassIcon class="h-4 w-4" />
              </Button>
            </div>
            <div class="mt-4">
              <pre class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"><code>&lt;Button&gt;
  &lt;PlusIcon class="mr-2 h-4 w-4" /&gt; Add Item
&lt;/Button&gt;

&lt;Button variant="secondary"&gt;
  &lt;DownloadIcon class="mr-2 h-4 w-4" /&gt; Download
&lt;/Button&gt;

&lt;Button variant="ghost"&gt;
  &lt;SendIcon class="mr-2 h-4 w-4" /&gt; Send
&lt;/Button&gt;

&lt;Button variant="destructive"&gt;
  &lt;TrashIcon class="mr-2 h-4 w-4" /&gt; Delete
&lt;/Button&gt;

&lt;Button size="default"&gt;
  &lt;ArrowRightIcon class="mr-2 h-3 w-3" /&gt; Continue
&lt;/Button&gt;

&lt;Button size="icon"&gt;
  &lt;MagnifyingGlassIcon class="h-4 w-4" /&gt;
&lt;/Button&gt;</code></pre>
            </div>
          </div>
        </div>
        <!-- Form Components Section -->
        <div
          v-if="activeCategory === 'form'"
          class="space-y-8"
        >
          <div class="space-y-8 pb-8">
            <h1 class="text-2xl font-bold text-gray-900">Form Components</h1>
            <div>
              <h2 class="mb-4 text-lg font-medium text-gray-900">Input Fields</h2>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="w-3/4">
                  <InputField
                    name="text-input"
                    label="Text Input"
                    placeholder="Enter some text"
                  />
                </div>
                <div class="w-3/4">
                  <InputField
                    name="email-input"
                    label="Email Input"
                    type="email"
                    placeholder="example@email.com"
                  />
                </div>
                <div class="w-3/4">
                  <InputField
                    name="number-input"
                    label="Number Input"
                    type="number"
                    placeholder="0"
                  />
                </div>
                <div class="w-3/4">
                  <InputField
                    name="disabled-input"
                    label="Disabled Input"
                    placeholder="This input is disabled"
                    disabled
                  />
                </div>
              </div>
              <div class="mt-4">
                <pre class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"><code>&lt;InputField
  name="text-input"
  label="Text Input"
  placeholder="Enter some text"
/&gt;</code></pre>
              </div>
            </div>

            <div>
              <h2 class="mb-4 text-lg font-medium text-gray-900">Select Fields</h2>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="w-3/4">
                  <SelectField
                    name="select-field"
                    label="Select Option"
                    :options="[
                      { value: 'option1', title: 'Option 1' },
                      { value: 'option2', title: 'Option 2' },
                      { value: 'option3', title: 'Option 3' }
                    ]"
                  />
                </div>
                <div class="w-3/4">
                  <MultiSelectField
                    name="multi-select-field"
                    label="Multi Select"
                    :options="[
                      { value: 'option1', title: 'Option 1' },
                      { value: 'option2', title: 'Option 2' },
                      { value: 'option3', title: 'Option 3' }
                    ]"
                  />
                </div>
              </div>
              <div class="mt-4">
                <pre class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"><code>&lt;SelectField
  name="select-field"
  label="Select Option"
  :options="[{value: 'option1', title: 'Option 1'}, {value: 'option2', title: 'Option 2'}]"
/&gt;</code></pre>
              </div>
            </div>

            <div>
              <h2 class="mb-4 text-lg font-medium text-gray-900">Text Area</h2>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="w-3/4">
                  <TextAreaField
                    name="textarea-field"
                    label="Text Area"
                    placeholder="Enter longer text here..."
                  />
                </div>
              </div>
              <div class="mt-4">
                <pre class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"><code>&lt;TextAreaField
  name="textarea-field"
  label="Text Area"
  placeholder="Enter longer text here..."
/&gt;</code></pre>
              </div>
            </div>

            <div>
              <h2 class="mb-4 text-lg font-medium text-gray-900">Checkboxes</h2>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="w-3/4">
                  <CheckboxField
                    name="checkbox-field"
                    label="Checkbox Option"
                  />
                  <CheckboxField
                    name="checkbox-field-2"
                    label="Another Checkbox"
                  />
                  <CheckboxField
                    name="checkbox-field-3"
                    label="Disabled Checkbox"
                    disabled
                  />
                </div>
              </div>
              <div class="mt-4">
                <pre
                  class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
                ><code>&lt;!-- Form Component Checkbox --&gt;
&lt;CheckboxField
  name="checkbox-field"
  label="Checkbox Option"
/&gt;
</code></pre>
              </div>
            </div>

            <div>
              <h2 class="mb-4 text-lg font-medium text-gray-900">Date Picker</h2>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="w-3/4">
                  <DatePickerField
                    name="date-field"
                    label="Date Picker"
                  />
                </div>
              </div>
              <div class="mt-4">
                <pre
                  class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
                ><code>&lt;DatePickerField
  name="date-field"
  label="Date Picker"
/&gt;</code></pre>
              </div>
            </div>

            <div>
              <h2 class="mb-4 text-lg font-medium text-gray-900">File Upload</h2>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="w-3/4">
                  <FileField
                    name="file-field"
                    label="File Upload"
                  />
                </div>
                <div class="w-3/4">
                  <FileField
                    name="file-field-custom"
                    label="Custom File Types"
                    :allowed-file-extensions="['pdf', 'doc', 'docx']"
                  />
                </div>
              </div>
              <div class="mt-4">
                <pre class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"><code>&lt;FileField
  name="file-field"
  label="File Upload"
/&gt;

&lt;FileField
  name="file-field-custom"
  label="Custom File Types"
  :allowed-file-extensions="['pdf', 'doc', 'docx']"
/&gt;</code></pre>
              </div>
            </div>

            <div>
              <h2 class="mb-4 text-lg font-medium text-gray-900">Multi File Field</h2>
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="w-3/4">
                  <MultiFileField
                    name="multi-file-field"
                    label="Multi File Upload"
                    :allowed-file-extensions="['pdf', 'doc', 'docx', 'jpg', 'png']"
                  />
                </div>
                <div class="w-3/4">
                  <MultiFileField
                    name="multi-file-field-limited"
                    label="Limited Files (Max 3)"
                    :allowed-file-extensions="['pdf', 'jpg', 'png']"
                    :max-files="3"
                  />
                </div>
              </div>
              <div class="mt-4">
                <pre
                  class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
                ><code>&lt;MultiFileField
  name="multi-file-field"
  label="Multi File Upload"
  :allowed-file-extensions="['pdf', 'doc', 'docx', 'jpg', 'png']"
/&gt;

&lt;MultiFileField
  name="multi-file-field-limited"
  label="Limited Files (Max 3)"
  :allowed-file-extensions="['pdf', 'jpg', 'png']"
  :max-files="3"
/&gt;</code></pre>
              </div>
            </div>

            <div>
              <h2 class="mb-4 text-lg font-medium text-gray-900">Form Layout</h2>
              <div class="w-full">
                <FormGrid>
                  <div class="col-span-6 sm:col-span-3">
                    <InputField
                      name="first-name"
                      label="First Name"
                      placeholder="John"
                    />
                  </div>
                  <div class="col-span-6 sm:col-span-3">
                    <InputField
                      name="last-name"
                      label="Last Name"
                      placeholder="Doe"
                    />
                  </div>
                  <div class="col-span-6">
                    <TextAreaField
                      name="address"
                      label="Address"
                      placeholder="Enter your address"
                    />
                  </div>
                  <div class="col-span-6 sm:col-span-3">
                    <SelectField
                      name="country"
                      label="Country"
                      :options="[
                        { value: 'us', title: 'United States' },
                        { value: 'ca', title: 'Canada' },
                        { value: 'uk', title: 'United Kingdom' }
                      ]"
                    />
                  </div>
                  <div class="col-span-6 sm:col-span-3">
                    <DatePickerField
                      name="birth-date"
                      label="Birth Date"
                    />
                  </div>
                </FormGrid>
              </div>
              <div class="mt-4">
                <pre class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"><code>&lt;FormGrid&gt;
  &lt;div class="col-span-6 sm:col-span-3"&gt;
    &lt;InputField name="first-name" label="First Name" placeholder="John" /&gt;
  &lt;/div&gt;
  &lt;div class="col-span-6 sm:col-span-3"&gt;
    &lt;InputField name="last-name" label="Last Name" placeholder="Doe" /&gt;
  &lt;/div&gt;
  &lt;div class="col-span-6"&gt;
    &lt;TextAreaField name="address" label="Address" placeholder="Enter your address" /&gt;
  &lt;/div&gt;
  &lt;!-- More form fields --&gt;
&lt;/FormGrid&gt;</code></pre>
              </div>
            </div>
          </div>
        </div>

        <!-- Shadcn UI Components Section -->
        <div
          v-if="activeCategory === 'inputs'"
          class="space-y-8"
        >
          <div>
            <h1 class="mb-4 text-2xl font-bold text-gray-900">Shadcn UI Components</h1>
            <div class="max-w-lg space-y-4">
              <h2 class="mb-4 text-lg font-medium text-gray-900">Inputs</h2>
              <div>
                <label
                  for="standard-input"
                  class="mb-1 block text-sm font-medium text-gray-700"
                >
                  Standard Input
                </label>
                <Input
                  v-model="inputValue"
                  placeholder="Enter text here"
                />
              </div>
              <div>
                <label
                  for="disabled-input"
                  class="mb-1 block text-sm font-medium text-gray-700"
                >
                  Disabled Input
                </label>
                <Input
                  disabled
                  placeholder="Disabled input"
                />
              </div>
              <div>
                <label
                  for="error-input"
                  class="mb-1 block text-sm font-medium text-red-700"
                >
                  Input with Custom Class
                </label>
                <Input
                  class="border-red-300 focus-visible:ring-red-500"
                  placeholder="Custom styled input"
                />
                <p class="mt-1 text-sm text-red-600">This field has custom styling</p>
              </div>
            </div>
            <div class="mt-4">
              <pre
                class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
              ><code>&lt;label for="standard-input" class="block text-sm font-medium text-gray-700 mb-1"&gt;Standard Input&lt;/label&gt;
&lt;Input v-model="inputValue" placeholder="Enter text here" /&gt;

&lt;label for="disabled-input" class="block text-sm font-medium text-gray-700 mb-1"&gt;Disabled Input&lt;/label&gt;
&lt;Input disabled placeholder="Disabled input" /&gt;

&lt;label for="error-input" class="block text-sm font-medium text-red-700 mb-1"&gt;Input with Custom Class&lt;/label&gt;
&lt;Input class="border-red-300 focus-visible:ring-red-500" placeholder="Custom styled input" /&gt;</code></pre>
            </div>
          </div>

          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Select Inputs</h2>
            <div class="max-w-lg space-y-4">
              <div>
                <label class="mb-1 block text-sm font-medium text-gray-700">Standard Select</label>
                <Select v-model="selectValue">
                  <SelectTrigger>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="option1">Option 1</SelectItem>
                    <SelectItem value="option2">Option 2</SelectItem>
                    <SelectItem value="option3">Option 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div class="mt-4">
              <pre
                class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
              ><code>&lt;Select v-model="selectValue"&gt;
  &lt;SelectTrigger&gt;
    &lt;SelectValue placeholder="Select an option" /&gt;
  &lt;/SelectTrigger&gt;
  &lt;SelectContent&gt;
    &lt;SelectItem value="option1"&gt;Option 1&lt;/SelectItem&gt;
    &lt;SelectItem value="option2"&gt;Option 2&lt;/SelectItem&gt;
    &lt;SelectItem value="option3"&gt;Option 3&lt;/SelectItem&gt;
  &lt;/SelectContent&gt;
&lt;/Select&gt;</code></pre>
            </div>
          </div>

          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Checkboxes & Radio Buttons</h2>
            <div class="space-y-6">
              <div class="flex items-center space-x-2">
                <Checkbox
                  id="terms"
                  v-model:checked="checkboxValue"
                />
                <label
                  for="terms"
                  class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Accept terms and conditions
                </label>
              </div>

              <div class="space-y-3">
                <label class="text-sm font-medium leading-none">Radio Options</label>
                <RadioGroup
                  v-model="radioValue"
                  class="space-y-1"
                >
                  <div class="flex items-center space-x-2">
                    <RadioGroupItem
                      id="r1"
                      value="option1"
                    />
                    <label
                      for="r1"
                      class="text-sm font-medium leading-none"
                    >
                      Radio option 1
                    </label>
                  </div>
                  <div class="flex items-center space-x-2">
                    <RadioGroupItem
                      id="r2"
                      value="option2"
                    />
                    <label
                      for="r2"
                      class="text-sm font-medium leading-none"
                    >
                      Radio option 2
                    </label>
                  </div>
                </RadioGroup>
              </div>

              <div class="flex items-center space-x-2">
                <Switch
                  id="airplane-mode"
                  v-model:checked="switchValue"
                />
                <label
                  for="airplane-mode"
                  class="text-sm font-medium leading-none"
                >
                  Toggle switch
                </label>
              </div>
            </div>
            <div class="mt-4">
              <pre
                class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
              ><code>&lt;!-- Checkbox --&gt;
&lt;div class="flex items-center space-x-2"&gt;
  &lt;Checkbox id="terms" v-model:checked="checkboxValue" /&gt;
  &lt;label for="terms" class="text-sm font-medium leading-none"&gt;Accept terms and conditions&lt;/label&gt;
&lt;/div&gt;

&lt;!-- Radio Group --&gt;
&lt;RadioGroup v-model="radioValue" class="space-y-1"&gt;
  &lt;div class="flex items-center space-x-2"&gt;
    &lt;RadioGroupItem value="option1" id="r1" /&gt;
    &lt;label for="r1" class="text-sm font-medium leading-none"&gt;Radio option 1&lt;/label&gt;
  &lt;/div&gt;
  &lt;div class="flex items-center space-x-2"&gt;
    &lt;RadioGroupItem value="option2" id="r2" /&gt;
    &lt;label for="r2" class="text-sm font-medium leading-none"&gt;Radio option 2&lt;/label&gt;
  &lt;/div&gt;
&lt;/RadioGroup&gt;

&lt;!-- Switch --&gt;
&lt;div class="flex items-center space-x-2"&gt;
  &lt;Switch id="airplane-mode" v-model:checked="switchValue" /&gt;
  &lt;label for="airplane-mode" class="text-sm font-medium leading-none"&gt;Toggle switch&lt;/label&gt;
&lt;/div&gt;</code></pre>
            </div>
          </div>
        </div>

        <!-- Cards Section -->
        <div
          v-if="activeCategory === 'cards'"
          class="space-y-8"
        >
          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Basic Card</h2>
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardContent>
                  <h3 class="text-lg font-medium">Card Title</h3>
                  <p class="mt-2 text-sm text-muted-foreground">
                    This is a basic card component with a title and description content.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Card with Header</CardTitle>
                  <CardDescription>This is a description for the card.</CardDescription>
                </CardHeader>
                <CardContent>
                  <p class="text-sm text-muted-foreground">
                    Card content goes here. This can include text, images, or other components.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Card with Footer</CardTitle>
                </CardHeader>
                <CardContent>
                  <p class="text-sm text-muted-foreground">
                    This card includes a button for actions in the footer.
                  </p>
                </CardContent>
                <CardFooter>
                  <Button>Action</Button>
                </CardFooter>
              </Card>
            </div>
            <div class="mt-4">
              <pre class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"><code>&lt;Card&gt;
  &lt;CardContent&gt;
    &lt;h3 class="text-lg font-medium"&gt;Card Title&lt;/h3&gt;
    &lt;p class="mt-2 text-sm text-muted-foreground"&gt;
      This is a basic card component with a title and description content.
    &lt;/p&gt;
  &lt;/CardContent&gt;
&lt;/Card&gt;

&lt;Card&gt;
  &lt;CardHeader&gt;
    &lt;CardTitle&gt;Card with Header&lt;/CardTitle&gt;
    &lt;CardDescription&gt;This is a description for the card.&lt;/CardDescription&gt;
  &lt;/CardHeader&gt;
  &lt;CardContent&gt;
    &lt;p class="text-sm text-muted-foreground"&gt;
      Card content goes here.
    &lt;/p&gt;
  &lt;/CardContent&gt;
&lt;/Card&gt;

&lt;Card&gt;
  &lt;CardHeader&gt;
    &lt;CardTitle&gt;Card with Footer&lt;/CardTitle&gt;
  &lt;/CardHeader&gt;
  &lt;CardContent&gt;...&lt;/CardContent&gt;
  &lt;CardFooter&gt;
    &lt;Button&gt;Action&lt;/Button&gt;
  &lt;/CardFooter&gt;
&lt;/Card&gt;</code></pre>
            </div>
          </div>
        </div>

        <!-- Accordion Section -->
        <div
          v-if="activeCategory === 'accordion'"
          class="space-y-8"
        >
          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Accordion Component</h2>
            <div class="max-w-lg">
              <Accordion
                type="single"
                collapsible
              >
                <AccordionItem value="item-1">
                  <AccordionTrigger>Is it accessible?</AccordionTrigger>
                  <AccordionContent>
                    Yes. It adheres to the WAI-ARIA design pattern.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger>Is it styled?</AccordionTrigger>
                  <AccordionContent>
                    Yes. It comes with default styles that match the other components' aesthetic.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger>Is it animated?</AccordionTrigger>
                  <AccordionContent>
                    Yes. It's animated by default, but you can disable it if you prefer.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            <div class="mt-4">
              <pre
                class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
              ><code>&lt;Accordion type="single" collapsible&gt;
  &lt;AccordionItem value="item-1"&gt;
    &lt;AccordionTrigger&gt;Is it accessible?&lt;/AccordionTrigger&gt;
    &lt;AccordionContent&gt;
      Yes. It adheres to the WAI-ARIA design pattern.
    &lt;/AccordionContent&gt;
  &lt;/AccordionItem&gt;
  &lt;AccordionItem value="item-2"&gt;
    &lt;AccordionTrigger&gt;Is it styled?&lt;/AccordionTrigger&gt;
    &lt;AccordionContent&gt;
      Yes. It comes with default styles that match the other components' aesthetic.
    &lt;/AccordionContent&gt;
  &lt;/AccordionItem&gt;
&lt;/Accordion&gt;</code></pre>
            </div>
          </div>
        </div>

        <!-- Alerts & Badges Section -->
        <div
          v-if="activeCategory === 'alerts'"
          class="space-y-8"
        >
          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Alert Dialog</h2>
            <div class="max-w-lg">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="default">Show Alert Dialog</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your account and
                      remove your data from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction>Continue</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <div class="mt-4">
              <pre class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"><code>&lt;AlertDialog&gt;
  &lt;AlertDialogTrigger asChild&gt;
    &lt;Button variant="ghost"&gt;Show Alert Dialog&lt;/Button&gt; &lt;/AlertDialogTrigger&gt;
  &lt;AlertDialogContent&gt;
    &lt;AlertDialogHeader&gt;
      &lt;AlertDialogTitle&gt;Are you absolutely sure?&lt;/AlertDialogTitle&gt;
      &lt;AlertDialogDescription&gt;
        This action cannot be undone. This will permanently delete your account
        and remove your data from our servers.
      &lt;/AlertDialogDescription&gt;
    &lt;/AlertDialogHeader&gt;
    &lt;AlertDialogFooter&gt;
      &lt;AlertDialogCancel&gt;Cancel&lt;/AlertDialogCancel&gt;
      &lt;AlertDialogAction&gt;Continue&lt;/AlertDialogAction&gt;
    &lt;/AlertDialogFooter&gt;
  &lt;/AlertDialogContent&gt;
&lt;/AlertDialog&gt;</code></pre>
            </div>
          </div>

          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Badges</h2>
            <div class="flex flex-wrap gap-4">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="destructive">Destructive</Badge>
            </div>
            <div class="mt-4">
              <pre
                class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
              ><code>&lt;Badge&gt;Default&lt;/Badge&gt;
&lt;Badge variant="secondary"&gt;Secondary&lt;/Badge&gt;
&lt;Badge variant="outline"&gt;Outline&lt;/Badge&gt;
&lt;Badge variant="destructive"&gt;Destructive&lt;/Badge&gt;</code></pre>
            </div>
          </div>
        </div>

        <!-- Tabs Section -->
        <div
          v-if="activeCategory === 'tabs'"
          class="space-y-8"
        >
          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Tabs Component</h2>
            <div class="max-w-lg">
              <Tabs
                default-value="account"
                class="w-full"
              >
                <TabsList class="grid w-full grid-cols-2">
                  <TabsTrigger value="account">Account</TabsTrigger>
                  <TabsTrigger value="password">Password</TabsTrigger>
                </TabsList>
                <TabsContent
                  value="account"
                  class="rounded-b-md border p-4"
                >
                  <p class="text-sm">
                    Make changes to your account here. Click save when you're done.
                  </p>
                </TabsContent>
                <TabsContent
                  value="password"
                  class="rounded-b-md border p-4"
                >
                  <p class="text-sm">
                    Change your password here. After saving, you'll be logged out.
                  </p>
                </TabsContent>
              </Tabs>
            </div>
            <div class="mt-4">
              <pre
                class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"
              ><code>&lt;Tabs default-value="account" class="w-full"&gt;
  &lt;TabsList class="grid w-full grid-cols-2"&gt;
    &lt;TabsTrigger value="account"&gt;Account&lt;/TabsTrigger&gt;
    &lt;TabsTrigger value="password"&gt;Password&lt;/TabsTrigger&gt;
  &lt;/TabsList&gt;
  &lt;TabsContent value="account" class="p-4 border rounded-b-md"&gt;
    &lt;p class="text-sm"&gt;Make changes to your account here. Click save when you're done.&lt;/p&gt;
  &lt;/TabsContent&gt;
  &lt;TabsContent value="password" class="p-4 border rounded-b-md"&gt;
    &lt;p class="text-sm"&gt;Change your password here. After saving, you'll be logged out.&lt;/p&gt;
  &lt;/TabsContent&gt;
&lt;/Tabs&gt;</code></pre>
            </div>
          </div>
        </div>

        <!-- Tables Section -->
        <div
          v-if="activeCategory === 'tables'"
          class="space-y-8"
        >
          <div>
            <h2 class="mb-4 text-lg font-medium text-gray-900">Table Component</h2>
            <div class="max-w-4xl">
              <Table>
                <TableCaption>A list of your recent invoices.</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead class="w-[100px]">Invoice</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead class="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell class="font-medium">INV001</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell>Credit Card</TableCell>
                    <TableCell class="text-right">$250.00</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell class="font-medium">INV002</TableCell>
                    <TableCell>Pending</TableCell>
                    <TableCell>PayPal</TableCell>
                    <TableCell class="text-right">$150.00</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell class="font-medium">INV003</TableCell>
                    <TableCell>Unpaid</TableCell>
                    <TableCell>Bank Transfer</TableCell>
                    <TableCell class="text-right">$350.00</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <div class="mt-4">
              <pre class="overflow-auto rounded-md bg-gray-50 p-4 text-sm"><code>&lt;Table&gt;
  &lt;TableCaption&gt;A list of your recent invoices.&lt;/TableCaption&gt;
  &lt;TableHeader&gt;
    &lt;TableRow&gt;
      &lt;TableHead class="w-[100px]"&gt;Invoice&lt;/TableHead&gt;
      &lt;TableHead&gt;Status&lt;/TableHead&gt;
      &lt;TableHead&gt;Method&lt;/TableHead&gt;
      &lt;TableHead class="text-right"&gt;Amount&lt;/TableHead&gt;
    &lt;/TableRow&gt;
  &lt;/TableHeader&gt;
  &lt;TableBody&gt;
    &lt;TableRow&gt;
      &lt;TableCell class="font-medium"&gt;INV001&lt;/TableCell&gt;
      &lt;TableCell&gt;Paid&lt;/TableCell&gt;
      &lt;TableCell&gt;Credit Card&lt;/TableCell&gt;
      &lt;TableCell class="text-right"&gt;$250.00&lt;/TableCell&gt;
    &lt;/TableRow&gt;
    &lt;!-- More rows... --&gt;
  &lt;/TableBody&gt;
&lt;/Table&gt;</code></pre>
            </div>
          </div>
        </div>

        <!-- Placeholder for other categories -->
        <div
          v-if="
            ![
              'buttons',
              'form',
              'inputs',
              'cards',
              'accordion',
              'alerts',
              'tabs',
              'tables'
            ].includes(activeCategory)
          "
          class="py-12 text-center"
        >
          <h2 class="mb-2 text-lg font-medium text-gray-900">
            {{ categories.find((c) => c.id === activeCategory)?.name }}
          </h2>
          <p class="text-gray-500">
            This section is under development. More components will be added soon.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  /* Add any component-specific styles here */
</style>
